import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import styled from 'styled-components';

import { t } from 'i18next';
import { CompetencyGoal } from 'generated/graphql';
import CollapseIcon from 'packages/kakapo-ui-kit/assets/icons/CollapseIcon';
import { Goal, GoalStatus } from '../../generated/graphql';
import { Box } from 'reflexbox';
import { IconButton, Skeleton } from '@treams/treams-ui';
import { PencilIcon } from '@treams/treams-icons';

const StyledGoalHeader = styled.header`
	display: flex;
	background-color: ${({ theme }) => theme.colors.whiteBlue};
	padding: 15px;
	flex-direction: row;
	justify-content: left;
	align-items: center;
	column-gap: 0.8rem;

	border-bottom: 1px solid ${({ theme }) => theme.colors.lightBlue};
`;

const GoalTitle = styled.h2`
	margin: 0;
	font-size: 14px;
	font-family: ${({ theme }) => theme.fontFamilies.heading};
`;

const Collapse = styled.button`
	border: none;
	background-color: transparent;
	padding: 0;
	margin: 0;
	display: block;
	transform: rotate(90deg);
	transition: transform 0.15s ease-in-out;
	border-radius: 50%;
	flex-grow: 0;
	flex-shrink: 0;

	${({ collapsed }: { collapsed: boolean }) => collapsed && 'transform: rotate(0deg);'}

	&:hover {
		background-color: ${({ theme }) => theme.colors.primaryLight};
	}

	svg {
		position: relative;
		display: block;
	}
`;

const GoalDescription = styled(Box)`
	flex-grow: 1;
	display: flex;
	column-gap: 0.5rem;
`;

const GoalCompletion = styled.div<{ status: GoalStatus }>`
	width: 16px;
	height: 16px;
	border-radius: 50%;
	flex-shrink: 0;
	flex-grow: 0;
	background-color: ${({ status, theme }) => {
		switch (status) {
			case GoalStatus.Archived:
				return theme.colors.lightBlue;
			case GoalStatus.Active:
				return theme.colors.yellow;
			case GoalStatus.Completed:
				return theme.colors.greenDark;
		}
	}};
`;

const GoalCompentencyContainer = styled.div`
	color: ${({ theme }) => theme.colors.slateBlue};
`;

function GoalCompetency({ goalCompetency }: { goalCompetency: CompetencyGoal }) {
	const { localize } = useLocalize();
	const { competency, competencyLevelIndex } = goalCompetency;
	const competencyLevelTitle = competency.competencyLevelSet.levels[competencyLevelIndex!].title;

	return (
		<GoalCompentencyContainer>
			{t('developmentPlan.index.goalCompetency', {
				title: localize(competency.title).text,
				level: localize(competencyLevelTitle).text,
			})}
		</GoalCompentencyContainer>
	);
}

export default Skeleton(
	function GoalHeader({
		onToggleCollapse,
		goal,
		collapsed,
		onEdit,
	}: {
		onToggleCollapse: () => void;
		goal: Goal;
		collapsed: boolean;
		onEdit: (goal: Goal) => void;
	}) {
		const { localize } = useLocalize();

		return (
			<StyledGoalHeader>
				<Collapse onClick={onToggleCollapse} collapsed={collapsed}>
					<CollapseIcon />
				</Collapse>
				<GoalCompletion status={goal.status} />
				<GoalDescription flexDirection={['column', 'column', 'row']}>
					<GoalTitle>{localize(goal.title).text}</GoalTitle>
					{goal.competency && <GoalCompetency goalCompetency={goal.competency} />}
				</GoalDescription>
				<IconButton
					type="plain"
					onClick={() => onEdit(goal)}
					icon={<PencilIcon />}
					ml={2}
					flexGrow={0}
					flexShrink={0}
				/>
			</StyledGoalHeader>
		);
	},
	function GoalHeaderSkeleton() {
		return <Skeleton.Box height={40} />;
	},
);
